import "./relationshipView.scss";
import "./relationshipPopover.scss";

import { clone, isNil } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import SplitButton from "../../components/form/SplitButton";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import { RELATIONSHIP_ROW_Y_PADDING } from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { RELATIONSHIP_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import PubSubTopics from "../../constants/pubSubTopics";
import tableDropAction from "../../constants/tableDropAction";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { typeSelectors } from "../../modules/definition/types";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { wrapEditingComponent } from "../../utils/codeSplit";
import {
  isDragMeasuringTimespan,
  relationshipDropPositionWithNesting,
  relationshipDropRowIndex,
  relationshipDropRowPreviousStartDate
} from "../../utils/dropFunctions";
import RelationshipRowCardView from "./RelationshipRowCardView";

// import debugUtils from "../../utils/debugUtils";

const canDropOnComponent = (props, dragItem, positions) => {
  if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
    return false;
  }
  const dropRows = props.data.rows;
  const mouseY = positions.targetPointerPos.y;
  const result = relationshipDropPositionWithNesting(
    dropRows,
    mouseY,
    dragItem,
    props.canItemDropOnRow,
    RELATIONSHIP_ROW_Y_PADDING
  );

  const newDropRowIndex =
    result.dropAction != tableDropAction.DropOnRow ? relationshipDropRowIndex(dropRows, mouseY, dragItem) : null;
  const dropDate =
    result.dropAction != tableDropAction.DropOnRow
      ? relationshipDropRowPreviousStartDate(dropRows, newDropRowIndex)
      : null;

  const canDropItems = props.requestCanDropItems(
    dragItem.multipleItemIds,
    dropRows,
    dropDate,
    result.dropAction,
    result.dropRow,
    newDropRowIndex,
    props.adjustItemEndWhenChangingStart
  );
  const canDrop = canDropItems.canDrop;

  return canDrop && props.canDrop;
};

const dropCallbacks = {
  onEnter: (props, dragItem, _dropTarget) => {
    if (props.onDragEnter) {
      props.onDragEnter(dragItem.multipleItemIds);
    }
  },

  onLeave: (props, _dragItem, _dropTarget) => {
    if (props.onDragLeave) {
      props.onDragLeave();
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.sortedItemIds.filter(id => props.selectedItemIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    const canDrop = canDropOnComponent(props, dragItem, positions);
    if (canDrop) {
      dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
    } else {
      dropTarget.component.cancelDrop();
    }
  },

  getDropPreviewForItem: (props, dragItem, dropTarget) => {
    return dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
  }
};

class RelationshipCardCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onScrollToDateReceived = this.onScrollToDateReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };

    this._horizontalScrollerActive = false;
    this._verticalScrollerActive = false;

    this._domNode = null;
    this._transformNode = null;
    this._liveLeft = 0;
    this._liveTop = 0;
    this._dropRowIndex = null;
    this._dropDate = null;

    this.getStyles = this.getStyles.bind(this);
    this.applyStyles = this.applyStyles.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);
    this.getDropPreviewForItem = this.getDropPreviewForItem.bind(this);
  }

  componentDidMount() {
    this.applyStyles();
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubScrollToDateToken = PubSub.subscribe(PubSubTopics.scrollToDate, this.onScrollToDateReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this.props.registerDropTarget(this, dropCallbacks);
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("RelationshipCardCanvasView", this.props, nextProps, true);
    if (nextProps.sortedItemIds !== this.props.sortedItemIds) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
  }

  componentDidUpdate() {
    this.applyStyles();
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubScrollToDateToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.props.data.rows.map(row => row.get("itemDisplay")),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onHover(_props, dragItem, positions, _dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return {
        message: "Cannot be dropped in relationship view",
        messageType: "error"
      };
    }

    const dropRows = this.props.data.rows;
    const mouseY = positions.targetPointerPos.y;
    const result = relationshipDropPositionWithNesting(
      dropRows,
      mouseY,
      dragItem,
      this.props.canItemDropOnRow,
      RELATIONSHIP_ROW_Y_PADDING
    );

    const newDropRowIndex = relationshipDropRowIndex(dropRows, mouseY, dragItem);
    if (result.dropAction != tableDropAction.DropOnRow) {
      if (this._dropRowIndex != newDropRowIndex) {
        this._dropRowIndex = newDropRowIndex;
        this._dropDate = relationshipDropRowPreviousStartDate(dropRows, newDropRowIndex);
      }
    } else {
      this._dropRowIndex = null;
      this._dropDate = null;
    }

    const canDropItems = this.props.requestCanDropItems(
      dragItem.multipleItemIds,
      dropRows,
      this._dropDate,
      result.dropAction,
      result.dropRow,
      newDropRowIndex,
      this.props.adjustItemEndWhenChangingStart
    );

    const canDrop = canDropItems.canDrop;
    if (!canDrop) {
      this._dropRow = null;
      this._dropAction = null;
      this.props.onLiveRowDrag
        ? this.props.onLiveRowDrag(dragItem.multipleItemIds, this._dropRow, this._dropAction)
        : null;
      return {
        message: canDropItems.message,
        messageType: "error"
      };
    }

    this._dropRow = result.dropRow;
    this._dropAction = result.dropAction;

    // And notify whatever is listening to the table that this shit is going down...
    this.props.onLiveRowDrag
      ? this.props.onLiveRowDrag(dragItem.multipleItemIds, this._dropRow, this._dropAction)
      : null;

    if (canDrop) {
      return {
        message: canDropItems.message,
        messageType: "success"
      };
    }
  }

  onDrop(_props, dragItem, _positions, _dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return false;
    }

    // Now move the dragged item to the correct index...
    this.props.onRowDrop ? this.props.onRowDrop(dragItem, this._dropRow, this._dropAction) : null;
    this._dropRow = null;
    this._dropAction = null;
  }

  cancelDrop() {
    this.props.onDragLeave ? this.props.onDragLeave() : null;
    this._dropRow = null;
    this._dropAction = null;
  }

  getDropPreviewForItem(_props, dragItem, _dropTarget) {
    const data = this.props.data;
    const row = data.rows.find(r => r.itemDisplay.id === dragItem.itemId);
    if (row) {
      const obj = row.itemDisplay.toObject();
      const rect = row.rect.merge({
        top: 0
      });

      return (
        <div className="relationshipCardDragContainer">
          <RelationshipRowCardView
            viewId={this.props.viewId}
            key={obj.id}
            rect={rect}
            columnData={row.columnData}
            colGroups={data.colGroups}
            {...obj}
            titleMetrics={row.itemDisplay.get(data.titleMetricsKey)}
            fontSize={this.props.fontSize}
            hasChildren={row.hasChildren}
            childrenExpanded={row.childrenExpanded}
            indentationLevel={row.indentationLevel}
            relationshipViewSettings={this.props.relationshipViewSettings}
            onClick={() => {}}
            onToggleChildren={null}
            onLabelChange={null}
            canDrag={false}
            drawDropAction={null}
            isDraggingRow={false}
            autoEditWhenBlank={false}
            typesById={this.props.typesById}
          />
        </div>
      );
    }
    return null;
  }

  getDragStartParams(_props, _dragItem, positions, _dropTarget) {
    const screenPointerPos = clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    return {
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
  }

  scrollToFoldedItem(itemId) {
    // Check if the event is folded in a parent event,
    // try to expend to scroll to the target item
    const { itemsById, data, onToggleChildren } = this.props;
    const { rows } = data;
    let itemIdsToExpand = [];

    const findToExpandSuper = id => {
      const targetItem = itemsById.get(id);
      if (targetItem && targetItem.super) {
        const superId = targetItem.super;
        itemIdsToExpand.push(superId);
        if (!rows.find(r => r.id === superId)) {
          // The super is not in position (i.e not expanded)
          // find the super of super
          findToExpandSuper(superId);
        }
      }
    };

    findToExpandSuper(itemId);
    if (itemIdsToExpand.length > 0) {
      onToggleChildren(itemIdsToExpand, true);
      // See https://aeontimeline.atlassian.net/browse/ATR-1670
      if (itemIdsToExpand.every(id => this.props.data.rows.find(r => r.itemDisplay.id === id))) {
        this.scrollToItem(itemId, true);
      }
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToItem(data.id);
  }

  isSequenceType(typeId) {
    const type = this.props.types.find(t => t.id === typeId);
    return typeSelectors.isSequenceType(type);
  }

  scrollToItem(itemId, doNotScrollAgain = false) {
    // Tries to scroll to sequence items only
    if (this.isSequenceType(this.props.itemsById.get(itemId).type)) {
      const row = this.props.data.rows.find(row => row.itemDisplay.id === itemId);
      if (row) {
        this.props.yScrollActions.animateToViewport(null, row.rect.top);
      } else if (!doNotScrollAgain) {
        this.scrollToFoldedItem(itemId);
      }
    }
  }

  onScrollToDateReceived(_msg, date) {
    // Scroll to the first row on or after the specified date
    if (date) {
      const row = this.props.data.rows.find(row =>
        isDateValid(row.itemDisplay.startDate) ? row.itemDisplay.startDate.timestamp >= date.timestamp : false
      );
      if (row) {
        this.scrollToItem(row.itemDisplay.id);
        return;
      }
      // If all items are before the specified date, scroll to the last row with a date
      const lastDatedRow = this.props.data.rows.findLast(row => isDateValid(row.itemDisplay.startDate));
      if (lastDatedRow) {
        this.scrollToItem(lastDatedRow.itemDisplay.id);
      }
    }
  }

  onLiveScrollStart(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = true;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = true;
    }
    this.applyStyles();
  }

  onLiveScrollUpdate(scrollLeft, scrollTop) {
    // Update both liveTop and liveLeft as needed, but we only need to applyStyles when
    // we are changing top.
    if (!isNil(scrollLeft) && scrollLeft !== this._liveLeft) {
      this._liveLeft = scrollLeft;
    }
    if (!isNil(scrollTop) && scrollTop !== this._liveTop) {
      this._liveTop = scrollTop;
      this.applyStyles();
    }
  }

  onLiveScrollStop(horizontalScroller, verticalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = false;
    }
    if (verticalScroller) {
      this._verticalScrollerActive = false;
    }
    this.applyStyles();
  }

  applyStyles() {
    const styles = this.getStyles();
    this._domNode.style.left = styles.nodeStyle.left;
    this._domNode.style.top = styles.nodeStyle.top;
    this._domNode.style.height = styles.nodeStyle.height;
    this._domNode.style.overflow = styles.nodeStyle.overflow;
    this._domNode.style.position = styles.nodeStyle.position;
    this._transformNode.style.transform = styles.transformStyle.transform;
  }

  getStyles() {
    // Node styles
    let left = `${this._liveLeft}px`;
    let top = "0px";
    let height = null;
    let overflow = null;
    let position = "absolute";
    const maxWidth = `${this.props.containerRect.width}px`;

    // Transform style
    let transform = null;

    if (this._horizontalScrollerActive) {
      left = `${this.props.containerRect.left}px`;
      top = `${this.props.containerRect.top}px`;
      height = `${this.props.containerRect.height}px`;
      position = "fixed";
      overflow = "hidden";
      transform = `translateY(${-this._liveTop}px)`;
    }

    const nodeStyle = {
      left,
      top,
      height,
      overflow,
      position,
      maxWidth
    };
    const transformStyle = {
      transform
    };

    return {
      nodeStyle,
      transformStyle
    };
  }

  getVisibleRows(rows) {
    const scrollTop = this.props.yScrollPosition.viewport.min;
    const scrollBottom = this.props.yScrollPosition.viewport.max;
    const visible = rows.filter(e => {
      return e.getIn(["rect", "bottom"]) > scrollTop && e.getIn(["rect", "top"]) < scrollBottom;
    });
    return visible;
  }

  onCreateItem() {
    this.props.onCreateItem(this.props.defaultType, true);
  }

  renderAddRow(visible) {
    let addRowClasses = ["relationshipTableRow", "relationshipAddRow"];
    let addRowTop = 0;
    const lastItem = visible.last();
    if (lastItem) {
      addRowTop = lastItem.rect.bottom;
    }
    if (lastItem && lastItem.itemDisplay.index % 2 === 0) {
      addRowClasses.push("odd");
    }
    const minHeight =
      this.props.containerRect.height - addRowTop > 0 ? this.props.containerRect.height - addRowTop : "auto";
    const style = { minHeight: `${minHeight}px`, transform: `translateY(${addRowTop}px)` };

    return (
      <div style={style} className={addRowClasses.join(" ")}>
        <div className="rEventCol">
          <SplitButton
            label={`Create ${this.props.defaultTypeLabel}`}
            icon="sidebarAdd"
            id="addRelationshipEvent"
            linkStyle={true}
            onClick={this.onCreateItem}
          />
        </div>
      </div>
    );
  }

  render() {
    const data = this.props.data;

    const visible = this.getVisibleRows(data.rows);
    const styles = this.getStyles();

    let result = (
      <div
        onClick={this.props.onViewClick}
        className="relationshipCardCanvasView"
        style={styles.nodeStyle}
        ref={node => (this._domNode = node)}
      >
        <div className="relationshipCardTransformView" ref={node => (this._transformNode = node)}>
          {visible.map(e => {
            const obj = e.itemDisplay.toObject();
            const isDragging = this.props.draggedRowIds && this.props.draggedRowIds.contains(obj.id);
            const rect = e.rect.merge({
              top: e.rect.top,
              bottom: e.rect.bottom
            });
            return (
              <RelationshipRowCardView
                viewId={this.props.viewId}
                key={obj.id}
                rect={rect}
                columnData={e.columnData}
                colGroups={data.colGroups}
                {...obj}
                isActiveView={this.props.isActiveView}
                selected={this.props.selectedItemIds.indexOf(obj.id) >= 0}
                titleMetrics={e.itemDisplay.get(data.titleMetricsKey)}
                fontSize={this.props.fontSize}
                hasChildren={e.hasChildren}
                childrenExpanded={e.childrenExpanded}
                indentationLevel={e.indentationLevel}
                relationshipViewSettings={this.props.relationshipViewSettings}
                onClick={this.props.onItemClick}
                onToggleChildren={this.props.onToggleChildren}
                onLabelChange={this.props.onLabelChange}
                canDrag={this.props.allowRowDrag}
                drawDropAction={
                  this.props.drawDropRow && this.props.drawDropRow.itemDisplay.id === e.itemDisplay.id
                    ? this.props.drawDropAction
                    : null
                }
                isDraggingRow={isDragging}
                autoEditWhenBlank={this.props.autoEditBlankNodes}
                onShowInspector={this.props.onShowInspector}
                typesById={this.props.typesById}
              />
            );
          })}
          {wrapEditingComponent(this.renderAddRow(visible))}
        </div>
      </div>
    );
    return result;
  }
}

RelationshipCardCanvasView.propTypes = {
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  fontSize: PropTypes.number.isRequired,
  data: PropTypeShapes.relationshipData.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  allowedReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  allowedInverseReferences: PropTypeShapes.allowedReferencePerSubjectObjectMap.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  onViewClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,

  typesById: ImmutablePropTypes.map.isRequired,

  drawDropRow: PropTypeShapes.relationshipRow,
  drawDropAction: PropTypes.string,
  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  onShowInspector: PropTypes.func,

  // Can the user drag rows
  allowRowDrag: PropTypes.bool,
  allowRowDragNesting: PropTypes.bool,
  draggedRowIds: ImmutablePropTypes.list,
  canDrop: PropTypes.bool.isRequired,

  // Dragging rows
  onDragLeave: PropTypes.func,
  onDragEnter: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,

  canTypeHaveSuperType: PropTypes.func.isRequired,
  canItemDropOnRow: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  types: ImmutablePropTypes.list.isRequired,
  defaultType: PropTypes.string.isRequired,
  defaultTypeLabel: PropTypes.string.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default withScrollPositionContext(
  withContainerContext(asDropTarget(RelationshipCardCanvasView, RELATIONSHIP_VIEW))
);
